import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchSpaceById } from '../api/spaceApi';
import Logo from '../componets/Logo';
import { Helmet } from 'react-helmet-async';

interface Space {
  id: string;
  description: string;
  ownerName: string;
  location: string;
  mainImageUrl: string;
  additionalImages?: string[];
  tags: string[];
  reservationCount: number;
  price: number;
}

const SpaceDetail: React.FC = () => {
  const { spaceId } = useParams<{ spaceId: string }>();
  const [space, setSpace] = useState<Space | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (spaceId) {
      fetchSpaceById(spaceId)
        .then(setSpace)
        .catch((error) => console.error('Failed to fetch space:', error));
    }
  }, [spaceId]);

  if (!space) return <div>Loading...</div>;

  const images = [space.mainImageUrl, ...(space.additionalImages || [])].slice(0, 4);

  // 모바일 터치 이벤트
  const handleTouchStart = (e: React.TouchEvent) => setTouchStartX(e.changedTouches[0].clientX);
  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEndX = e.changedTouches[0].clientX;
    const distance = touchStartX - touchEndX;
    if (distance > 50 && currentImageIndex < images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    } else if (distance < -50 && currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Helmet>
        <title>{space.ownerName} | BIN-GO</title>
        <meta name="description" content={space.description || '공간 상세 설명입니다.'}/>
        <meta property="og:title" content={space.ownerName} />
        <meta property="og:description" content={space.description} />
        <meta property="og:image" content={space.mainImageUrl} />
        <meta property="og:url" content={`https://web.bin-go.kr/spaces/${space.id}`} />
      </Helmet>
      <Logo />
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6 mt-6">
        {/* 이미지 영역 */}
        <div
          className="relative w-full h-64 mb-4 overflow-hidden rounded-lg"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <img
            src={images[currentImageIndex]}
            alt={`space ${currentImageIndex}`}
            className="w-full h-full object-cover"
          />

          {/* Dot 인디케이터 */}
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                className={`w-2 h-2 rounded-full ${
                  currentImageIndex === idx ? 'bg-blue-500' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col">
          <h1 className="text-xl font-bold leading-none">{space.ownerName}님</h1>
          <p className="text-gray-600 text-xs mb-3">{space.location}</p>
        </div>
        <p className="text-gray-800 mb-1">
          <strong>예약안내:</strong>
        </p>
        <div className="border border-gray-300 rounded p-4 mb-4 whitespace-pre-line">
          {space.description}
        </div>
        <p className="text-gray-800 mb-4">
          <strong>요금 :</strong> {space.price.toLocaleString()}원 / 일
        </p>
        <div className="mb-6">
          <strong>TAG</strong>
        <div className="mb-1.5 mt-1 text-blue-500"> 
            <div className="flex flex-wrap space-x-1">
              {space.tags?.map((tag, index) => (
                <span
                  key={index}
                  className="px-1.5 py-1 bg-blue-100 text-blue-600 rounded-full text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-center mt-8">
          <button
            onClick={() => {
              const token = localStorage.getItem('user_token');
              if (!token) {
                alert('로그인이 필요합니다.');
                navigate('/login');
                return;
              }
              navigate(`/spaces/${spaceId}/reservation-form`);
            }}
            className="bg-blue-500 text-white px-6 py-2 rounded"
          >
            예약하기
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpaceDetail;
